import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";

import type { AppTicketDto, FastMenuActionItemDto, SelfDto, TicketFiltersDto, TicketTabsStatsDto } from "@/api/types";
import { ActivityDot } from "@/components/ActivityDot/ActivityDot";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Asterisk01Icon } from "@/components/Icons/Asterisk01Icon";
import { BarChart02Icon } from "@/components/Icons/BarChart02Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { TicketVisibilityIndicator } from "@/components/Ticket/TicketVisibilityIndicator";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { CollectionEmptyState, CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Stack } from "@/design-system/Stack";
import {
  createDashboardColumnHelper,
  type DashboardTableState,
  TABLE_COLUMN_SIZES,
} from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { Text } from "@/design-system/Text";
import { useBool } from "@/hooks/useBool";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { CollectionControls } from "@/product-patterns/tickets/collections/CollectionControls";
import { ListPageToolbar } from "@/product-patterns/tickets/collections/ListPageToolbar";
import { ResponsiveCollection } from "@/product-patterns/tickets/collections/ResponsiveCollection";
import { TableCellCount, TableCellDescription } from "@/product-patterns/tickets/collections/TableCellContent";
import type { ResidentTicketTabs } from "@/queries/tickets/constants";
import { RESIDENT_CLOSED_TICKET_TAB, RESIDENT_OPEN_TICKET_TAB } from "@/queries/tickets/constants";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { canCreateTicket } from "../../permissions";
import { CreateTicketModal } from "../list/components/CreateTicketModal";
import { ResidentTicketCard } from "./components/ResidentTicketCard";
import type { ResidentTicketsFilterValues } from "./components/ResidentTicketsFilterDrawer";
import { ResidentTicketsFilterDrawer } from "./components/ResidentTicketsFilterDrawer";

export interface ResidentTicketListContentProps {
  tab: ResidentTicketTabs;
  onChangeTab: (tab: ResidentTicketTabs) => void;
  filter: ResidentTicketsFilterValues;
  onUpdateFilter: (filter: ResidentTicketsFilterValues) => void;
  search: string;
  onChangeSearch: (search: string) => void;
  sessionUser: SelfDto;
  totalTickets?: number;
  totalTicketsInTab?: number;
  tabsStats?: TicketTabsStatsDto;
  tickets: AppTicketDto[];
  isLoadingTickets: boolean;
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingMoreTickets: boolean;
  filterOptions: TicketFiltersDto;
  plusButtonOptions: FastMenuActionItemDto[];
}
export function ResidentTicketListContent({
  sessionUser,
  tab,
  onChangeTab,
  search,
  onChangeSearch,
  filter,
  onUpdateFilter,
  totalTickets,
  totalTicketsInTab,
  tabsStats,
  tickets,
  isLoadingTickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingMoreTickets,
  filterOptions,
  plusButtonOptions,
}: ResidentTicketListContentProps): React.ReactNode {
  const { t } = useTranslation();
  const [isFilterDrawerOpened, setIsFilterDrawerOpened] = useState(false);
  const [isCreateModalOpened, createModalHandler] = useBool(false);

  const handleFilterUpdate = useCallback(
    (values: ResidentTicketsFilterValues) => {
      onUpdateFilter({
        ...filter,
        ...values,
      });
    },
    [onUpdateFilter, filter],
  );

  const hasPermission = usePermission();
  const postHog = usePostHog();

  const handleCreateInternalTicket = useCallback(() => {
    postHog.capture("clicked_create_ticket");

    createModalHandler.setTrue();
  }, [createModalHandler, postHog]);

  const tabItems: TabItem<ResidentTicketTabs>[] = [
    {
      label: tabsStats == null ? t("page.resident-tickets.tabs.open-loading") : t("page.resident-tickets.tabs.open"),
      value: RESIDENT_OPEN_TICKET_TAB,
      count: tabsStats?.openTickets,
    },
    {
      label:
        tabsStats == null ? t("page.resident-tickets.tabs.closed-loading") : t("page.resident-tickets.tabs.closed"),
      value: RESIDENT_CLOSED_TICKET_TAB,
      count: tabsStats?.closedTickets,
    },
  ];

  const menuActions: ContextMenuItem[] = useMemo(() => {
    const actions: ContextMenuItem[] = [];

    plusButtonOptions.forEach((option) => {
      if (option.useType === "ticketFlow") {
        let callbackFn = handleCreateInternalTicket;
        let label = t("page.resident-tickets.create-ticket.actions.create-ticket");
        let icon = <Tool02Icon />;

        switch (option.type) {
          case "openExternalUrl":
            label = option.translations?.find((l) => l.language === sessionUser.language.id)?.text || "";
            icon = <Globe02Icon />;
            callbackFn = () => window.open(option.url, "_blank");

            break;
          case "openZiezodan":
            label = t("page.resident-tickets.create-ticket.actions.create-ticket-via-external", {
              externalParty: "Ziezodan",
            });
            callbackFn = () => window.open(option.url, "_blank");

            break;
          case "createTicket":
            label = option.maintainer
              ? t("page.resident-tickets.create-ticket.actions.create-ticket-for", {
                  maintainer: option.maintainer,
                })
              : t("page.resident-tickets.create-ticket.actions.create-ticket");

            break;
          case "createServiceRequest":
            label = option.maintainer
              ? t("page.resident-tickets.create-ticket.actions.create-service-request-for", {
                  maintainer: option.maintainer,
                })
              : t("page.resident-tickets.create-ticket.actions.create-service-request");

            icon = <Asterisk01Icon />;

            break;
        }

        actions.push({
          labels: {
            default: label,
          },
          value: option.id,
          icon,
          callbackFn,
        });
      }
    });

    return actions;
  }, [handleCreateInternalTicket, plusButtonOptions, sessionUser.language.id, t]);
  const amountActiveFilters = Object.entries(filter).filter(([key, value]) => {
    if (key === "visibility" && value === "both") {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value != null;
  }).length;
  const hasFilters = amountActiveFilters > 0 || Boolean(search);

  return (
    <>
      <DocumentPage
        tabTitle={t("page.resident-tickets.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.resident-tickets.title"),
              },
            ]}
          />
        }
        actions={
          hasPermission(canCreateTicket) && (
            <>
              {menuActions.length === 1 && (
                <Button data-testid="create-ticket-button" variant="filled" onClick={menuActions[0].callbackFn}>
                  {t("page.resident-tickets.create-btn")}
                </Button>
              )}
              {menuActions.length > 1 && (
                <ContextMenu
                  items={menuActions}
                  triggerComponent={
                    <Button data-testid="create-ticket-button">{t("page.resident-tickets.create-btn")}</Button>
                  }
                />
              )}
            </>
          )
        }
        header={
          <ListPageToolbar
            search={
              <SearchInput
                placeholder={t("page.resident-tickets.search.placeholder")}
                defaultValue={search}
                onChange={(e) => onChangeSearch(e.target.value)}
              />
            }
            filters={
              <Button variant="soft" onClick={() => setIsFilterDrawerOpened(true)} icon={<FilterFunnel01Icon />}>
                <Inline gap="sm" align="center" as="span">
                  {t("common.action.filter")}
                  {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
                </Inline>
              </Button>
            }
          />
        }
      >
        <Tabs.Root<ResidentTicketTabs> tabs={tabItems} activeTab={tab} onSelectTab={(tab) => onChangeTab(tab)}>
          <ResponsiveCollection
            controls={
              <CollectionControls
                summary={
                  totalTickets != null &&
                  totalTicketsInTab != null && (
                    <Text variant="caption">
                      <Trans
                        i18nKey={
                          hasFilters
                            ? "page.resident-tickets.table.results.filtered-label"
                            : "page.resident-tickets.table.results.label"
                        }
                        values={{ count: totalTickets, total: totalTicketsInTab }}
                        components={{ bold: <b /> }}
                      />
                    </Text>
                  )
                }
                options={
                  <Inline gap="xs" align="center" as="span">
                    <Text variant="caption-emphasis" tone="secondary">
                      {t("page.resident-tickets.table.sort.last-activity")}
                    </Text>
                    <span className="block rotate-90">
                      <BarChart02Icon />
                    </span>
                  </Inline>
                }
              />
            }
            wide={
              <TicketsTable
                sessionUser={sessionUser}
                hasFilters={hasFilters}
                tickets={tickets}
                hasMoreTickets={hasMoreTickets}
                loadMoreTickets={loadMoreTickets}
                isLoadingTickets={isLoadingTickets}
                isLoadingMoreTickets={isLoadingMoreTickets}
                tab={tab}
              />
            }
            compact={
              <TicketsMobileView
                sessionUser={sessionUser}
                hasFilters={hasFilters}
                tickets={tickets}
                hasMoreTickets={hasMoreTickets}
                loadMoreTickets={loadMoreTickets}
                isLoadingTickets={isLoadingTickets}
                isLoadingMoreTickets={isLoadingMoreTickets}
                tab={tab}
              />
            }
          />
        </Tabs.Root>
      </DocumentPage>

      <CreateTicketModal isOpened={isCreateModalOpened} onOpenChange={createModalHandler.set} isCreatorResident />
      <ResidentTicketsFilterDrawer
        isOpened={isFilterDrawerOpened}
        onOpenChange={setIsFilterDrawerOpened}
        filter={filter}
        onUpdate={handleFilterUpdate}
        filterOptions={filterOptions}
      />
    </>
  );
}
interface TicketsTableProps {
  sessionUser: ResidentTicketListContentProps["sessionUser"];
  tickets: AppTicketDto[];
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingTickets: boolean;
  isLoadingMoreTickets: boolean;
  tab: ResidentTicketTabs;
  hasFilters: boolean;
}
function TicketsTable({
  sessionUser,
  tickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingTickets,
  isLoadingMoreTickets,
  hasFilters,
  tab,
}: TicketsTableProps): React.ReactNode {
  const slug = useSlug();
  const { t, i18n } = useTranslation();

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AppTicketDto>();

    return [
      helper.display({
        id: "visibility",
        cell: (cell) => {
          const value = cell.row.original.visibility;

          if (value === "private") {
            return null;
          }

          return <TicketVisibilityIndicator type={value} />;
        },
        size: TABLE_COLUMN_SIZES.VISUAL_INDICATOR.DEFAULT,
        enableResizing: false,
        meta: {
          isVisualIndicator: true,
        },
      }),
      helper.accessor("status", {
        header: t("page.resident-tickets.table.header.status"),
        cell: (cell) => (
          <Inline gap="sm" align="center">
            {cell.row.original.unread && <ActivityDot />}
            <TicketStatus labelColor={cell.getValue().color} description={cell.getValue().name} variant="filled" />
          </Inline>
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("title", {
        header: t("page.resident-tickets.table.header.title"),
        cell: (cell) => (
          <TableCellDescription
            primary={cell.getValue()}
            secondary={formatDate(i18n, "datetime", cell.row.original.postedAt)}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("category", {
        header: t("page.resident-tickets.table.header.category"),
        cell: (cell) => cell.getValue().name,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("user", {
        header: t("page.resident-tickets.table.header.reporter"),
        cell: (cell) => <User user={cell.getValue()} shouldNavigate={false} />,
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("numberOfComments", {
        header: t("page.resident-tickets.table.header.activity"),
        cell: (cell) => <TableCellCount icon={<MessageCircle01Icon />}>{cell.getValue()}</TableCellCount>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("rating", {
        header: t("page.resident-tickets.table.header.rating"),
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <RatingBadge rating={value} />;
          }
        },
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.display({
        id: "rate-ticket",
        cell: (cell) =>
          cell.row.original.status.type === "closed" &&
          cell.row.original.author.id === sessionUser.id && (
            <span className="mr-space-sm inline-flex items-center gap-space-sm rounded-lg border border-rating-fill bg-rating-background p-space-sm">
              <Star01Icon tone="rating" />
              <span className="text-caption-emphasis whitespace-nowrap">{t("page.resident-tickets.table.rate")}</span>
            </span>
          ),
        minSize: 280,
        enableResizing: false,
      }),
    ];
  }, [i18n, sessionUser.id, t]);
  const tableState = useMemo(
    () =>
      ({
        columnVisibility: {
          rating: tab === RESIDENT_CLOSED_TICKET_TAB,
        },
      }) satisfies Partial<DashboardTableState>,
    [tab],
  );
  const table = useResizableTable(TABLE_KEYS.TICKETS_RESIDENT, {
    columns,
    data: tickets,
    enableSorting: false,
    state: tableState,
  });

  return (
    <>
      {(tickets.length > 0 || isLoadingTickets) && (
        <Table
          table={table}
          getRowHref={(row) =>
            routes.tickets.details({
              slug,
              id: row.original.id,
            })
          }
          isLoading={isLoadingTickets && !isLoadingMoreTickets}
        />
      )}
      {!isLoadingTickets && tickets.length === 0 && (
        <CollectionEmptyState>
          {hasFilters ? t("page.resident-tickets.table.no-results") : t("page.resident-tickets.table.empty")}
        </CollectionEmptyState>
      )}
      {hasMoreTickets && <CollectionLoadingState ref={ref} />}
    </>
  );
}
function TicketsMobileView({
  hasFilters,
  hasMoreTickets,
  isLoadingMoreTickets,
  isLoadingTickets,
  loadMoreTickets,
  tab,
  tickets,
}: TicketsTableProps) {
  const { t } = useTranslation();
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });

  return isLoadingTickets ? (
    <CollectionLoadingState />
  ) : (
    <>
      {tickets.length > 0 ? (
        <Stack gap="md">
          {tickets.map((ticket) => (
            <ResidentTicketCard key={ticket.id} ticket={ticket} showRating={tab === RESIDENT_CLOSED_TICKET_TAB} />
          ))}
        </Stack>
      ) : (
        <CollectionEmptyState presentation="compact">
          {hasFilters ? t("page.resident-tickets.table.no-results") : t("page.resident-tickets.table.empty")}
        </CollectionEmptyState>
      )}
      {hasMoreTickets && <CollectionLoadingState ref={ref} show={isLoadingTickets || isLoadingMoreTickets} />}
    </>
  );
}
